import { useCallback, useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteFeeOverride } from "@/features/merchant/api/use-delete-fee-override";
import { useFeeOverrides } from "@/features/merchant/api/use-fee-overrides";
import { FeeOverrideFormDialog } from "@/features/merchant/components/fee-overrides/fee-override-form-dialog";
import {
  feeOverridesColumnsFactory,
  type FeeOverrideRow,
} from "@/features/merchant/components/fee-overrides/fee-overrides-columns";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/**
 * Section « Dérogations tarifaires » d'un marchand : liste + création +
 * suppression (retour au barème standard).
 */
export function FeeOverridesSection({ merchantId }: { merchantId: string }) {
  const { t: tm } = useMerchantTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeeOverrideRow | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useFeeOverrides(merchantId, params);
  const deleteOverride = useDeleteFeeOverride(merchantId);

  const rows = useMemo(
    () => (data?.items ?? []).filter((r): r is FeeOverrideRow => Boolean(r.id)),
    [data]
  );

  const handleDelete = useCallback(
    (row: FeeOverrideRow) => setDeleteTarget(row),
    []
  );

  const columns = useMemo(
    () => feeOverridesColumnsFactory(handleDelete),
    [handleDelete]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.feeOverrides.title")}
        </h2>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tm("merchant.feeOverrides.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={columns}
        title={tm("merchant.feeOverrides.title")}
        exportFileName="merchant-fee-overrides"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <FeeOverrideFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        merchantId={merchantId}
      />

      <MutationConfirmPreview
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        data={{
          id: deleteTarget?.id,
          productCode: deleteTarget?.productCode,
          feeRuleId: deleteTarget?.feeRuleId,
        }}
        title={tm("common.mutationPreview.deleteTitle")}
        description={tm("common.mutationPreview.deleteDescription")}
        confirmLabel={tm("merchant.feeOverrides.actions.delete")}
        t={tm}
        pending={deleteOverride.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteOverride.mutate(deleteTarget.id, {
            onSuccess: () => {
              notifSilence(tm("merchant.feeOverrides.feedback.deleted"));
              setDeleteTarget(null);
            },
            onError: (error) => notifSilenceError(error.message),
          });
        }}
      />
    </section>
  );
}
